import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { setupService } from "../../../Core/Api/services";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { UserDataCustomType } from "../../../Core/Api/Types/user-data-custom-type";
import { UserDataStatus } from "../../../Core/Api/Types/user-data-status";
import { UserDataType } from "../../../Core/Api/Types/user-data-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetUploadCardStatus } from "../../../Redux/Reducers/redux-slice-aspera-upload";
import { GetObjectUploadedMediaFilesFormatsIds } from "../../../Redux/Reducers/redux-slice-asset";
import { useAppSelector } from "../../../Redux/redux-store";
import UploadCard, { UploadCardHandle, UploadContext } from "../../Cards/UploadCard/upload-card";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { FileLinkedType } from "../../../Core/Api/Types/file-linked-type";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalUploadMediaFilesElementProps = {
  objectId: number;
  objectType: ObjectType;
  onClose?: (props: CloseClbProps) => void;
  onSuccess?: () => void;
  defaultLinkedType?: FileLinkedType;
  context?: "drive" | "distribution";
};
export type ModalUploadMediaFilesProps = CommonModalProps<ModalUploadMediaFilesElementProps>;

const ModalUploadMediaFiles = React.forwardRef<any, ModalUploadMediaFilesProps>((props, ref) => {
  const { t } = useTranslation();
  const uploadCardIndex: number = 0;

  const { objectId, defaultLinkedType, closeModal, onClose, onSuccess, context, ...modalProps } = props;
  const [filesTypes, setFilesTypes] = React.useState<UploadCardFormatDto[]>([]);
  const [selectedFileType, setSelectedFileType] = React.useState<UploadCardFormatDto>();

  const [dataType, setDataType] = React.useState<UserDataType>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [dataStatus, setDataStatus] = React.useState<UserDataStatus>();
  const [dataCustomType, setDataCustomType] = React.useState<UserDataCustomType>();
  const uploadedFilesFormatsIds = useAppSelector((state) => GetObjectUploadedMediaFilesFormatsIds(state));
  const uploadCardStatus = useAppSelector((state) => GetUploadCardStatus(state, uploadCardIndex));

  const uploadCardRef = React.useRef<UploadCardHandle>(null);

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleUpload = () => {
    uploadCardRef.current?.performUpload(dataType, dataStatus, dataCustomType);
  };

  React.useEffect(() => {
    setupService.GetFormatsByLayerAndType(objectId).then(function (formats) {
      formats.forEach((format) => {
        // override cause we don't want user to be able to delete file after uploading it in this modal
        format.userHasRighToDelete = false;
      });

      setFilesTypes(formats);
      if (formats.find((format) => format.linkedType === defaultLinkedType)) {
        setSelectedFileType(formats.find((format) => format.linkedType === defaultLinkedType));
      }

      setIsLoading(false);
    });
  }, [objectId]);

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.uploadMediaFiles")}
      reference={ref}
      closeModal={handleCancelClick}
      width={500}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Button onClick={handleCancelClick}>{t("common:close")}</Button>
          {uploadCardStatus === "uploadDone" ? (
            <Button variant="contained" color="primary" onClick={handleCancelClick}>
              {t("button:done")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              disabled={
                (selectedFileType?.isUserDataMandatory && (!dataType || !dataStatus || !dataCustomType)) ||
                !selectedFileType ||
                uploadCardStatus !== "readyToUpload"
              }
              color="success"
              onClick={handleUpload}
            >
              {t("button:performUpload")}
            </Button>
          )}
        </Stack>
      }
    >
      {!isLoading && (
        <Box>
          {filesTypes && filesTypes.length > 0 ? (
            <>
              <Typography variant="body1" fontWeight={500} color="text.main">
                {t("modal:modalUploadMediaFiles.body.selectFileToUpload")}
              </Typography>

              <Autocomplete
                value={selectedFileType || null}
                onChange={(e, value) => {
                  value && setSelectedFileType(value);
                  !value && setSelectedFileType(undefined);
                }}
                options={filesTypes}
                getOptionLabel={(format: UploadCardFormatDto) =>
                  `${format.name} (${format.acceptsFiles["application/octet-stream"].map((ext) => `${ext}`)})`
                }
                getOptionDisabled={(option) =>
                  option.linkedType !== FileLinkedType.Thumbnail &&
                  uploadedFilesFormatsIds.includes(option.id) &&
                  props.objectType !== ObjectType.ScribeSubtitleProject
                }
                renderInput={(params) => <TextField {...params} />}
                sx={{ mt: -2 }}
              />
            </>
          ) : (
            <Alert severity="info">{t("alert:info.noFileTypeToUpload")}</Alert>
          )}
        </Box>
      )}
      {isLoading && <CircularProgress />}

      {selectedFileType?.isUserDataMandatory && (
        <Box mt={3}>
          <Typography variant="body1" fontWeight={500} color="text.main">
            {t("modal:modalUploadMediaFiles.body.fillFollowingInfos")}
          </Typography>
          <Autocomplete
            onChange={(e, value) => setDataType(value?.value)}
            options={GlobalConst.userDataTypes}
            renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.fileType")} />}
          />
          <Autocomplete
            onChange={(e, value) => setDataStatus(value?.value)}
            options={GlobalConst.userDataStatuses}
            renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.fileStatus")} />}
          />
          <Autocomplete
            onChange={(e, value) => setDataCustomType(value?.value)}
            options={GlobalConst.userDataCustomTypes}
            renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.customType")} />}
          />
        </Box>
      )}

      {selectedFileType && (
        <Box mt={3}>
          <UploadCard
            cardIndex={uploadCardIndex}
            ref={uploadCardRef}
            title={t("card:title.uploadYourFile")}
            objectId={objectId}
            format={selectedFileType}
            recommendedRatio={
              context === "drive" &&
              (selectedFileType.linkedType === FileLinkedType.Thumbnail ||
                selectedFileType.linkedType === FileLinkedType.PosterTitleMain)
                ? "16 / 9"
                : undefined
            }
            dropZoneOptions={{
              maxSize: selectedFileType.maxFileSizeMb * 1024 * 1024,
              maxFiles: 1,
            }}
            coverPlaceholderIcon={<UploadFileIcon />}
            userHasRightToDelete={selectedFileType.userHasRighToDelete}
            context={UploadContext.MediaFile}
            onSuccessUpload={() => onSuccess && onSuccess()}
          />
        </Box>
      )}
    </Modal>
  );
});

export default ModalUploadMediaFiles;
